import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ChevronLeft,
    ChevronRight,
    Eye,
    Globe,
    Image as ImageIcon,
    Link2,
    MoreHorizontal,
    Pencil,
    Trash2,
    Images,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

import { Product } from '../models/product.model';
import { ProductView } from './product-view.component';
import { resolveApplicationTypeLabel, dedupeApplicationTypes } from '../../../../common/utils/application-type';
import { config } from '../../../../common/hooks/use-settings-api.hook';
import { buildVisualizerUrl, PREVIEW_LINK_ATTRIBUTION } from '../../../../common/utils/visualizer-link';
import { PriceText } from '../../../../components/design-system/common/PriceText';

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];

/**
 * The small label that floats over a product photo.
 *
 * A photo is an unpredictable background, so these need their own translucent
 * ground rather than the plain outline a Badge normally carries.
 */
const OverlayBadge = ({ children }: { children: ReactNode }) => (
    <Badge
        variant="outline"
        className="h-5 rounded-md border-border bg-background/85 px-1.5 text-[10px] font-medium text-foreground backdrop-blur-sm"
    >
        {children}
    </Badge>
);

export const ProductsAsGrid = (props) => {
    const [selectedProductIdForView, setSelectedProductIDForView] = useState<any>(undefined)
    const [productViewAnchor, setProductViewAnchor] = useState(null)
    const [selectedProductForMenu, setSelectedProductForMenu] = useState<any>(null)
    const [t] = useTranslation();
    const navigate = useNavigate();

    // The parent hands over one page, already fetched from the server.
    const displayedProducts = props.filteredProducts;

    const totalCount = props.totalCount ?? props.filteredProducts.length;
    const firstShown = totalCount === 0 ? 0 : props.page * props.rowsPerPage + 1;
    const lastShown = Math.min(totalCount, (props.page + 1) * props.rowsPerPage);
    const lastPage = Math.max(0, Math.ceil(totalCount / props.rowsPerPage) - 1);

    return <>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
            {displayedProducts.map((product: Product, index) => (
                <div
                    key={index}
                    style={{ animationDelay: `${index * 50}ms` }}
                    className="flex h-full animate-in flex-col overflow-hidden rounded-xl border bg-card fill-mode-both fade-in slide-in-from-bottom-4 duration-300"
                >
                    {/* Product Image */}
                    <div
                        onClick={(e) => {
                            setProductViewAnchor(e.currentTarget);
                            setSelectedProductIDForView(product.uuid);
                        }}
                        className="relative h-[180px] w-full cursor-pointer overflow-hidden bg-muted"
                    >
                        {product.thumbnailPath ? (
                            <div
                                style={{ backgroundImage: `url(${product.thumbnailPath})` }}
                                className={cn(
                                    'absolute inset-0 bg-center transition-transform duration-300 hover:scale-105',
                                    (!product.familySlug || product.familySlug === 'surface-products')
                                        ? 'bg-cover'
                                        : 'bg-contain bg-no-repeat',
                                )}
                            />
                        ) : (
                            <div className="absolute inset-0 flex items-center justify-center bg-muted">
                                <ImageIcon className="size-14 text-muted-foreground" />
                            </div>
                        )}

                        {/* Selection Checkbox - Top Right */}
                        {props.isSelectionMode && (
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    props.onSelectProduct?.(product.id);
                                }}
                                className="absolute end-2 top-2 z-10 flex size-6 items-center justify-center rounded-md border border-border bg-background/90 backdrop-blur-sm"
                            >
                                <Checkbox checked={props.selectedProductIds?.has(product.id) || false} />
                            </div>
                        )}

                        {/* Variant Badge - Top Right (when not in selection mode) */}
                        {!props.isSelectionMode && product.siblings.length > 0 && (
                            <span className="absolute end-2 top-2 flex h-5 min-w-6 items-center justify-center bg-foreground px-1 text-[10px] font-medium text-background">
                                +{product.siblings.length}
                            </span>
                        )}

                        {/* Type and Size - Top Left */}
                        <div className="absolute start-2 top-2 flex flex-wrap gap-1">
                            {/* Draft badge — first in order so it's visually dominant */}
                            {product.isDraft && (
                                <Badge
                                    data-testid="product-draft-badge"
                                    className="h-5 border-transparent bg-warning px-1.5 text-[10px] font-medium tracking-wide text-white uppercase"
                                >
                                    {t('PAGES.MY_PRODUCTS.DRAFT_BADGE', 'Draft')}
                                </Badge>
                            )}
                            {product.type && <OverlayBadge>{product.type}</OverlayBadge>}
                            {(() => {
                                // Hide the size badge entirely when no dimensions are known
                                // (e.g. draft products from an integration sync). Previously
                                // rendered as the literal "undefined×undefined cm".
                                const dims = [product.width, product.length, product.height].filter(
                                    (v) => v != null && v > 0,
                                );
                                if (dims.length === 0) return null;
                                return (
                                    <OverlayBadge>
                                        {dims.join('×')} {product.unit || 'cm'}
                                    </OverlayBadge>
                                );
                            })()}
                        </div>

                        {/* Surface Badges - Bottom */}
                        <div className="absolute inset-x-2 bottom-2 flex flex-wrap gap-1">
                            {dedupeApplicationTypes(product.application_types).map((type, idx) => (
                                <OverlayBadge key={idx}>{resolveApplicationTypeLabel(t, type)}</OverlayBadge>
                            ))}
                        </div>
                    </div>

                    {/* Card Content */}
                    <div className="flex flex-1 flex-col p-2.5">
                        {/* Collection Badges */}
                        {product.attributes?.collections && product.attributes.collections.length > 0 && (
                            <div className="mb-2 flex flex-wrap gap-1">
                                {product.attributes.collections.slice(0, 2).map((collection, idx) => (
                                    <Badge
                                        key={idx}
                                        variant="outline"
                                        className="h-4 px-1.5 text-[9px] font-medium tracking-wide uppercase"
                                    >
                                        {collection}
                                    </Badge>
                                ))}
                                {product.attributes.collections.length > 2 && (
                                    <Badge
                                        variant="outline"
                                        className="h-4 px-1.5 text-[9px] font-medium text-muted-foreground"
                                    >
                                        +{product.attributes.collections.length - 2}
                                    </Badge>
                                )}
                            </div>
                        )}

                        {/* Product Name, and what shoppers see it sold for (nothing when unknown) */}
                        <div className="mb-auto">
                            <a
                                href={`/products/${product.uuid}/edit`}
                                onClick={(e) => {
                                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                                    e.preventDefault();
                                    props.handleEditProduct(product);
                                }}
                                className="line-clamp-2 cursor-pointer text-xs leading-snug font-medium text-foreground no-underline hover:text-muted-foreground"
                            >
                                {product.name}
                            </a>
                            <PriceText
                                price={product.pricing?.price}
                                className="mt-1 block text-xs text-muted-foreground"
                                data-testid="catalog-price"
                            />
                        </div>

                        {/* Action Buttons */}
                        <div className="mt-2 flex gap-1 border-t pt-2">
                            {product.link && (
                                <Button
                                    variant="outline"
                                    size="icon-sm"
                                    aria-label={t('PAGES.MY_PRODUCTS.OPEN_PRODUCT_LINK', 'Open product link') as string}
                                    onClick={() => window.open(product.link, '_blank')}
                                >
                                    <Link2 />
                                </Button>
                            )}
                            {product.sku && config?.alias && !product.isDraft && (
                                <Button
                                    variant="outline"
                                    size="icon-sm"
                                    title={t('PAGES.MY_PRODUCTS.MENU.PREVIEW')}
                                    onClick={() => {
                                        const url = buildVisualizerUrl({ alias: config.alias, sku: product.sku, attribution: PREVIEW_LINK_ATTRIBUTION });
                                        window.open(url, '_blank', 'noopener,noreferrer');
                                    }}
                                >
                                    <Eye />
                                </Button>
                            )}

                            {/* Product Actions Menu */}
                            <DropdownMenu
                                open={selectedProductForMenu?.uuid === product.uuid}
                                onOpenChange={(open) => setSelectedProductForMenu(open ? product : null)}
                            >
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="icon-sm"
                                        className="ms-auto"
                                        aria-label={t('PAGES.MY_PRODUCTS.PRODUCT_ACTIONS', 'Product actions') as string}
                                    >
                                        <MoreHorizontal />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-45">
                                    <DropdownMenuItem
                                        // The visibility row is a switch, not a command: selecting it
                                        // must not dismiss the menu the switch lives in.
                                        onSelect={(e) => e.preventDefault()}
                                        className="justify-between"
                                    >
                                        <span className="flex items-center gap-2">
                                            <Globe className="size-4 text-muted-foreground" />
                                            {t('PAGES.MY_PRODUCTS.MENU.VISIBILITY')}
                                        </span>
                                        <Switch
                                            checked={selectedProductForMenu?.isVisible || false}
                                            onCheckedChange={() => {
                                                if (selectedProductForMenu) {
                                                    // Call the handler to save changes
                                                    props.handleProductVisibilityChange(selectedProductForMenu);
                                                    // Update local state for UI responsiveness (preserve class instance)
                                                    const updated = Object.create(Object.getPrototypeOf(selectedProductForMenu));
                                                    Object.assign(updated, selectedProductForMenu);
                                                    updated.setIsVisible(!selectedProductForMenu.isVisible);
                                                    setSelectedProductForMenu(updated);
                                                }
                                            }}
                                        />
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onSelect={() => {
                                            props.handleEditProduct(product);
                                        }}
                                    >
                                        <Pencil className="text-muted-foreground" />
                                        {t('PAGES.MY_PRODUCTS.MENU.EDIT')}
                                    </DropdownMenuItem>
                                    {product.sku && config?.alias && !product.isDraft && (
                                        <DropdownMenuItem
                                            onSelect={() => {
                                                const url = buildVisualizerUrl({ alias: config.alias, sku: product.sku, attribution: PREVIEW_LINK_ATTRIBUTION });
                                                window.open(url, '_blank', 'noopener,noreferrer');
                                            }}
                                        >
                                            <Eye className="text-muted-foreground" />
                                            {t('PAGES.MY_PRODUCTS.MENU.PREVIEW')}
                                        </DropdownMenuItem>
                                    )}
                                    {/* Pictures, a clip and posts for this product (feat/room-to-revenue). */}
                                    {product.uuid && !product.isDraft && (
                                        <DropdownMenuItem onSelect={() => navigate(`/marketing/content-packs?make=${product.uuid}`)}>
                                            <Images className="text-muted-foreground" />
                                            {t('CONTENT_PACKS.ENTRY')}
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        variant="destructive"
                                        onSelect={() => {
                                            props.onDelete(product);
                                        }}
                                    >
                                        <Trash2 />
                                        {t('PAGES.MY_PRODUCTS.MENU.DELETE')}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </div>
            ))}
        </div>

        {/* Pagination */}
        <div className="mt-4 flex flex-wrap items-center justify-end gap-x-6 gap-y-3 rounded-xl border bg-card px-4 py-3">
            <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                    {t('PAGES.MY_PRODUCTS.PAGINATION.ROWS_PER_PAGE')}
                </span>
                <Select
                    value={String(props.rowsPerPage)}
                    // The page owns pagination and reads `event.target.value`, so the
                    // handler keeps the shape it was written against.
                    onValueChange={(value) => props.handleChangeRowsPerPage({ target: { value } })}
                >
                    <SelectTrigger size="sm" className="w-18">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {ROWS_PER_PAGE_OPTIONS.map((option) => (
                            <SelectItem key={option} value={String(option)}>
                                {option}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <span className="text-xs text-muted-foreground">
                {firstShown}-{lastShown} {t('PAGES.MY_PRODUCTS.PAGINATION.OF')} {totalCount}
            </span>

            <div className="flex items-center gap-1">
                <Button
                    variant="outline"
                    size="icon-sm"
                    aria-label={t('PAGES.MY_PRODUCTS.PAGINATION.PREVIOUS') as string}
                    disabled={props.page <= 0}
                    onClick={() => props.handleChangePage(null, props.page - 1)}
                >
                    <ChevronLeft className="rtl:rotate-180" />
                </Button>
                <Button
                    variant="outline"
                    size="icon-sm"
                    aria-label={t('PAGES.MY_PRODUCTS.PAGINATION.NEXT') as string}
                    disabled={props.page >= lastPage}
                    onClick={() => props.handleChangePage(null, props.page + 1)}
                >
                    <ChevronRight className="rtl:rotate-180" />
                </Button>
            </div>
        </div>

        {selectedProductIdForView && <ProductView selectedProductForViewID={selectedProductIdForView} setSelectedProductForView={setSelectedProductIDForView} anchorEl={productViewAnchor} setAnchorEl={setProductViewAnchor} />}
    </>
}
